import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";
import type { Json } from "@how/database";
import { parseBrand, type Brand } from "@how/website-core";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

/**
 * The church's brand, as the public site reads it. `tenant_settings.branding` is a free jsonb column that other
 * settings may come to share, so the row is read whole and only the theme's keys are written back.
 */
export function useBrand() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["brand", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<{ brand: Brand; stored: Record<string, unknown> }> => {
      const { data, error } = await supabase
        .from("tenant_settings")
        .select("branding")
        .eq("tenant_id", tenantId ?? "")
        .maybeSingle();
      if (error) throw error;
      const stored =
        typeof data?.branding === "object" &&
        data.branding !== null &&
        !Array.isArray(data.branding)
          ? (data.branding as Record<string, unknown>)
          : {};
      return { brand: parseBrand(stored), stored };
    },
  });
}

export function useSaveBrand() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly brand: Brand;
      readonly stored: Record<string, unknown>;
    }) => {
      const { brand, stored } = input;
      // Everything already in the column is kept; only the keys the theme reads are replaced.
      const branding: Record<string, unknown> = {
        ...stored,
        palette: brand.palette,
        typeface: brand.typeface,
        accent: brand.accent,
        logoWidth: brand.logoWidth,
        // How the site opens (ADR 0073, amended 2026-10-14): off, the spinner, or the welcome words.
        intro: brand.intro,
      };
      if (brand.logoPath === undefined) delete branding.logoPath;
      else branding.logoPath = brand.logoPath;
      const { error } = await supabase
        .from("tenant_settings")
        .update({ branding: branding as Json })
        .eq("tenant_id", tenantId ?? "");
      if (error !== null) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ["brand", tenantId] });
    },
  });
}
